import moment from "moment";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
// Imported by module path rather than through the services/store barrels, which
// sit in long import cycles.
import { useGetAnalyticsSelectionQuery } from "@/shared/services/analytics/analytics.query";
import { captureSentryError } from "@/shared/utils/sentry-helpers";
import { useAnalyticsStoreShallow } from "../store/analytics.store";

// The custom-range sentinel the date filter uses for anything that isn't one of
// its presets.
const CUSTOM_DATE_INDEX = -1;
// Matches the date picker's own floor, so the widened range stays selectable.
const MAX_LOOKBACK_YEARS = 2;

/**
 * Labels and scopes a deep-linked selection (`/pages/analytics?story=&version=`).
 *
 * The ids arrive through the store's initial state, but their titles do not, and
 * the filter pills fall back to "All stories" / "Version" whenever a title is
 * empty — which is how a correctly filtered page came to look completely
 * unfiltered. Two things were wrong:
 *
 *  - titles were recovered from the ClickHouse VERSIONS rows, which are bounded
 *    by the selected date window, so a version with no events in the last 30
 *    days stayed nameless. They now come from MongoDB, which has no window.
 *  - the default window is the last 30 days, so an older version's sessions were
 *    out of range and every widget read empty. The range is widened to cover the
 *    version's whole life.
 */
export const useDeepLinkSelection = () => {
  const { story, version, date, isDeepLink, setMultipleFilters } =
    useAnalyticsStoreShallow((s) => ({
      story: s.story,
      version: s.version,
      date: s.date,
      isDeepLink: s.isDeepLink,
      setMultipleFilters: s.setMultipleFilters,
    }));

  const needsTitles =
    isDeepLink &&
    Boolean((story.id && !story.title) || (version.id && !version.title));

  const { data, isError } = useGetAnalyticsSelectionQuery({
    storyId: story.id,
    versionId: version.id,
    enabled: needsTitles,
  });

  // One application per mount: setMultipleFilters feeds back into the state this
  // effect reads, and re-running would fight the user's own filter changes.
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!data || appliedRef.current) return;
    appliedRef.current = true;

    // Nothing resolved: the story/version was deleted, belongs to another org,
    // or sits in a folder this user cannot see. Drop the filter instead of
    // leaving them in front of a silently empty dashboard.
    if (!data.story && !data.version) {
      setMultipleFilters({
        story: { id: "", title: "" },
        version: { id: "", title: "" },
      });
      toast.error("That story or version is no longer available.");
      return;
    }

    const next: Parameters<typeof setMultipleFilters>[0] = {};

    if (data.story) {
      next.story = { id: data.story.id, title: data.story.title };
    }
    if (data.version) {
      next.version = { id: data.version.id, title: data.version.title };
    }

    // A version created before the current window starts would have no rows in
    // range. Widen back to its creation (one day of slack for timezone skew),
    // capped at the date picker's own two-year floor.
    if (data.version?.createdAt) {
      const floor = moment().subtract(MAX_LOOKBACK_YEARS, "years");
      const createdAt = moment(data.version.createdAt).subtract(1, "day");
      const from = moment.max(createdAt, floor);
      if (from.isBefore(moment(date.dateFrom))) {
        next.date = {
          dateFrom: from.format("YYYY-MM-DD"),
          dateTo: moment().add(1, "day").format("YYYY-MM-DD"),
          index: CUSTOM_DATE_INDEX,
        };
      }
    }

    setMultipleFilters(next);
    // date is read to compare against, not to react to — a user changing the
    // range must not re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, setMultipleFilters]);

  // A failed lookup is a soft failure: the filter itself is still applied, and
  // the versions table's own title backfill can still name a version that has
  // recent sessions. Report it instead of toasting — otherwise every deep link
  // during a deploy window (frontend live before the endpoint) would nag.
  useEffect(() => {
    if (!isError) return;
    captureSentryError(new Error("Analytics deep-link selection lookup failed"), {
      level: "warning",
      tags: { area: "analytics", reason: "deep-link-selection" },
      extra: { storyId: story.id, versionId: version.id },
    });
  }, [isError, story.id, version.id]);
};
